import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Upload, Plus, Edit, Trash2, ArrowLeft, Download } from 'lucide-react';
import api from '../../services/api';

const HolidayManagement = () => {
    const navigate = useNavigate();
    const [holidays, setHolidays] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [showAddModal, setShowAddModal] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchHolidays();
    }, [selectedYear]);

    const fetchHolidays = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/holidays?year=${selectedYear}`);
            setHolidays(res.data);
        } catch (error) {
            console.error('Error fetching holidays:', error);
            alert('Failed to fetch holidays');
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (e) => {
        e.preventDefault();
        if (!uploadFile) {
            alert('Please select a file');
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append('file', uploadFile);

        try {
            const res = await api.post('/holidays/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            alert(res.data.message);
            setUploadFile(null);
            fetchHolidays();
        } catch (error) {
            console.error('Upload error:', error);
            alert(error.response?.data?.error || 'Failed to upload file');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteHoliday = async (id) => {
        if (!window.confirm('Are you sure you want to delete this holiday?')) return;

        try {
            await api.delete(`/holidays/${id}`);
            alert('Holiday deleted successfully');
            fetchHolidays();
        } catch (error) {
            console.error('Delete error:', error);
            alert('Failed to delete holiday');
        }
    };

    const downloadSampleTemplate = () => {
        const csvContent = 'date,name,type,description\n2026-01-26,Republic Day,PUBLIC,National Holiday\n2026-03-08,Holi,PUBLIC,Festival of Colors\n2026-08-15,Independence Day,PUBLIC,National Holiday';
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'holiday_template.csv';
        a.click();
    };

    return (
        <div className="container section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <button
                    onClick={() => navigate('/dashboard')}
                    style={{ display: 'flex', alignItems: 'center', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}
                >
                    <ArrowLeft size={20} style={{ marginRight: '0.5rem' }} /> Back
                </button>

                <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                    Holiday Management
                </h1>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                        style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border)' }}
                    >
                        {[2024, 2025, 2026, 2027].map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Upload Section */}
            <div className="glass-card" style={{ padding: '1.5rem', marginBottom: '2rem' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Upload size={20} /> Upload Holiday Calendar
                </h2>
                <p style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '1rem' }}>
                    Upload an Excel (.xlsx, .xls) or CSV file with columns: date, name, type (optional), description (optional)
                </p>

                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={(e) => setUploadFile(e.target.files[0])}
                        style={{ flex: 1, minWidth: '200px' }}
                    />
                    <button
                        onClick={handleFileUpload}
                        disabled={!uploadFile || uploading}
                        className="btn btn-primary"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                    >
                        <Upload size={18} />
                        {uploading ? 'Uploading...' : 'Upload'}
                    </button>
                    <button
                        onClick={downloadSampleTemplate}
                        className="btn btn-secondary"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                    >
                        <Download size={18} />
                        Download Template
                    </button>
                </div>
            </div>

            {/* Holidays List */}
            <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                        Holidays for {selectedYear}
                    </h2>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="btn btn-primary"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                    >
                        <Plus size={18} /> Add Holiday
                    </button>
                </div>

                {loading ? (
                    <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
                        Loading holidays...
                    </div>
                ) : holidays.length === 0 ? (
                    <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
                        No holidays found for {selectedYear}. Upload a calendar or add holidays manually.
                    </div>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                            <tr>
                                <th style={{ padding: '1rem', textAlign: 'left' }}>Date</th>
                                <th style={{ padding: '1rem', textAlign: 'left' }}>Holiday Name</th>
                                <th style={{ padding: '1rem', textAlign: 'left' }}>Type</th>
                                <th style={{ padding: '1rem', textAlign: 'left' }}>Description</th>
                                <th style={{ padding: '1rem', textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {holidays.map(holiday => (
                                <tr key={holiday.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                    <td style={{ padding: '1rem', fontWeight: '500' }}>
                                        {new Date(holiday.date).toLocaleDateString('en-IN', {
                                            weekday: 'short',
                                            day: 'numeric',
                                            month: 'short'
                                        })}
                                    </td>
                                    <td style={{ padding: '1rem' }}>{holiday.name}</td>
                                    <td style={{ padding: '1rem' }}>
                                        <span style={{
                                            padding: '0.25rem 0.75rem',
                                            borderRadius: '999px',
                                            fontSize: '0.75rem',
                                            fontWeight: '600',
                                            background: holiday.type === 'PUBLIC' ? '#dbeafe' : '#fef3c7',
                                            color: holiday.type === 'PUBLIC' ? '#1e40af' : '#92400e'
                                        }}>
                                            {holiday.type}
                                        </span>
                                    </td>
                                    <td style={{ padding: '1rem', color: '#6b7280' }}>
                                        {holiday.description || '-'}
                                    </td>
                                    <td style={{ padding: '1rem', textAlign: 'right' }}>
                                        <button
                                            onClick={() => handleDeleteHoliday(holiday.id)}
                                            style={{ color: '#ef4444', border: 'none', background: 'none', cursor: 'pointer' }}
                                            title="Delete Holiday"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Add Holiday Modal */}
            {showAddModal && (
                <AddHolidayModal
                    onClose={() => setShowAddModal(false)}
                    onSuccess={() => {
                        setShowAddModal(false);
                        fetchHolidays();
                    }}
                    year={selectedYear}
                />
            )}
        </div>
    );
};

// Add Holiday Modal Component
const AddHolidayModal = ({ onClose, onSuccess, year }) => {
    const [formData, setFormData] = useState({
        date: '',
        name: '',
        type: 'PUBLIC',
        description: ''
    });
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await api.post('/holidays', formData);
            alert('Holiday added successfully');
            onSuccess();
        } catch (error) {
            console.error('Add holiday error:', error);
            alert(error.response?.data?.error || 'Failed to add holiday');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
            <div style={{ background: 'white', padding: '2rem', borderRadius: '1rem', width: '100%', maxWidth: '500px' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>Add Holiday</h2>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                        <label htmlFor="holiday-date" style={{ display: 'block', marginBottom: '0.5rem' }}>Date</label>
                        <input
                            id="holiday-date"
                            type="date"
                            required
                            value={formData.date}
                            onChange={e => setFormData({ ...formData, date: e.target.value })}
                            style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '0.5rem' }}
                        />
                    </div>
                    <div>
                        <label htmlFor="holiday-name" style={{ display: 'block', marginBottom: '0.5rem' }}>Holiday Name</label>
                        <input
                            id="holiday-name"
                            type="text"
                            required
                            value={formData.name}
                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                            style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '0.5rem' }}
                        />
                    </div>
                    <div>
                        <label htmlFor="holiday-type" style={{ display: 'block', marginBottom: '0.5rem' }}>Type</label>
                        <select
                            id="holiday-type"
                            value={formData.type}
                            onChange={e => setFormData({ ...formData, type: e.target.value })}
                            style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '0.5rem' }}
                        >
                            <option value="PUBLIC">Public Holiday</option>
                            <option value="RESTRICTED">Restricted Holiday</option>
                            <option value="OPTIONAL">Optional Holiday</option>
                        </select>
                    </div>
                    <div>
                        <label htmlFor="holiday-description" style={{ display: 'block', marginBottom: '0.5rem' }}>Description (Optional)</label>
                        <textarea
                            id="holiday-description"
                            rows="2"
                            value={formData.description}
                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                            style={{ width: '100%', padding: '0.5rem', border: '1px solid #ddd', borderRadius: '0.5rem' }}
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
                        <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
                        <button type="submit" disabled={loading} className="btn btn-primary">
                            {loading ? 'Adding...' : 'Add Holiday'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default HolidayManagement;
